import { EkranBledu } from '@insolitum/ui';
import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Granica błędu całej powłoki — ostatnia linia przed białym ekranem.
 *
 * Bez niej wyjątek rzucony w renderze odmontowuje CAŁE drzewo Reacta
 * i człowiek zostaje z pustą stroną bez jednego zdania. Najczęstszy taki
 * wyjątek nie jest błędem kodu, tylko wdrożeniem: karta otwarta przed
 * nowym wydaniem prosi o leniwy kawałek (`lazyPage`), którego nazwa już nie
 * istnieje na serwerze (`ChunkLoadError` / „Failed to fetch dynamically
 * imported module"). „Odśwież" leczy go w całości — pobiera nowy
 * `index.html` z nowymi nazwami kawałków — dlatego jest akcją pierwszą.
 *
 * „Przejdź do pulpitu" to zwykły odnośnik, nie `<Link>`: granica stoi NAD
 * routerem (w `main.tsx`), więc kontekstu routera tu nie ma, a pełne
 * przeładowanie i tak jest tym, czego trzeba po awarii drzewa.
 *
 * Teksty są literałami PL, a nie `t()`: granica ma działać także wtedy, gdy
 * zawiódł któryś z dostawców pod nią, więc nie zależy od niczego poza
 * Reactem i `@insolitum/ui` — a zdania samego ekranu (`TRESCI_EKRANU_BLEDU`)
 * i tak są po polsku; tłumaczone same przyciski dałyby ekran w dwóch językach.
 *
 * Musi być klasą — React łapie błędy renderu wyłącznie przez
 * `getDerivedStateFromError` / `componentDidCatch`, hooki tego nie umieją.
 */
interface GranicaBleduProps {
  children: ReactNode;
}

interface GranicaBleduStan {
  blad: Error | null;
}

export class GranicaBledu extends Component<GranicaBleduProps, GranicaBleduStan> {
  state: GranicaBleduStan = { blad: null };

  static getDerivedStateFromError(blad: Error): GranicaBleduStan {
    return { blad };
  }

  componentDidCatch(blad: Error, info: ErrorInfo): void {
    // Konsola to jedyny ślad po awarii w przeglądarce klienta — bez stosu
    // komponentów zgłoszenie „biały ekran" nie ma od czego zacząć.
    console.error('[GranicaBledu] awaria drzewa powłoki', blad, info.componentStack);
  }

  render() {
    const { blad } = this.state;
    if (!blad) return this.props.children;

    return (
      <EkranBledu
        wariant="blad"
        uklad="pelny"
        akcje={
          <>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn btn--primary btn--lg"
            >
              Odśwież
            </button>
            <a href="/" className="btn btn--secondary btn--lg">
              Przejdź do pulpitu
            </a>
          </>
        }
        szczegoly={{
          // Nazwa błędu, nie komunikat: „ChunkLoadError" albo „TypeError"
          // da się przepisać ze zdjęcia ekranu, a komunikat bywa długi
          // i potrafi nieść adres z parametrami.
          kod: blad.name || 'Error',
          sciezka: window.location.pathname,
        }}
      />
    );
  }
}
